import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";
import { useAuth } from "@clerk/react";
import { authedFetch, readJson } from "./api";
import { contrastRatio, fontOptions } from "./model";

type Appearance = { fontFamily: string; accent: string };
const defaults: Appearance = { fontFamily: "system", accent: "#456554" };

function useAppearanceState() {
  const { userId, getToken, isLoaded, isSignedIn } = useAuth();
  const [state, setState] = useState<{ userId: string; saved: Appearance; draft: Appearance } | null>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const session = useRef({});

  useEffect(() => {
    session.current = {};
    setState(null);
    setError("");
    setMessage("");
    setSaving(false);
    if (!isLoaded || !isSignedIn || !userId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await authedFetch(getToken, "/api/me/appearance");
        const { appearance } = await readJson<{ appearance: Appearance }>(response);
        if (!cancelled) setState({ userId, saved: appearance, draft: appearance });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load appearance.");
      }
    })();
    return () => { cancelled = true; session.current = {}; };
  }, [getToken, isLoaded, isSignedIn, userId]);

  const current = state?.userId === userId ? state : null;
  const draft = current?.draft ?? defaults;
  function update(next: Appearance) {
    setState(previous => previous && previous.userId === userId ? { ...previous, draft: next } : previous);
    setMessage("");
  }
  async function save() {
    if (!current || saving) return;
    const savingSession = session.current;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const response = await authedFetch(getToken, "/api/me/appearance", {
        method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(draft)
      });
      const { appearance } = await readJson<{ appearance: Appearance }>(response);
      if (savingSession !== session.current) return;
      setState(previous => previous?.userId === current.userId ? { ...previous, saved: appearance, draft: appearance } : previous);
      setMessage("Appearance saved.");
    } catch (err) {
      if (savingSession === session.current) setError(err instanceof Error ? err.message : "Could not save appearance.");
    } finally { if (savingSession === session.current) setSaving(false); }
  }

  const ink = contrastRatio(draft.accent, "#ffffff") >= contrastRatio(draft.accent, "#171717") ? "#ffffff" : "#171717";
  const style = {
    "--font": fontOptions.find(font => font.value === draft.fontFamily)?.css ?? fontOptions[0].css,
    "--accent": draft.accent,
    "--accent-ink": ink,
    "--accent-text": contrastRatio(draft.accent, "#fbfaf7") >= 4.5 ? draft.accent : "#171717"
  } as CSSProperties;

  return { draft, style, update, save, error, saving, message, ready: !!current,
    dirty: !!current && JSON.stringify(current.saved) !== JSON.stringify(draft),
    cancel: () => { if (current) update(current.saved); },
    reset: () => update(defaults)
  };
}

const AppearanceContext = createContext<ReturnType<typeof useAppearanceState> | null>(null);

export function AccountAppearanceProvider({ children }: { children: ReactNode }) {
  const appearance = useAppearanceState();
  return <AppearanceContext.Provider value={appearance}>{children}</AppearanceContext.Provider>;
}

export function useAccountAppearance() {
  const appearance = useContext(AppearanceContext);
  if (!appearance) throw new Error("Account appearance provider is missing");
  return appearance;
}

export function LibraryAppearancePage() {
  const appearance = useAccountAppearance();
  return <AccountAppearance appearance={appearance} />;
}

export function AccountAppearance({ appearance }: { appearance: ReturnType<typeof useAccountAppearance> }) {
  return (
    <div className="account-appearance" style={appearance.style}>
      <section aria-labelledby="appearance-title" className="appearance-panel">
        <div>
          <p className="eyebrow">Make it yours</p>
          <h2 id="appearance-title">Library appearance</h2>
          <p>Your font and color for this library, including album names. Preview changes here, then save them to your account.</p>
        </div>
        <div className="appearance-sample" style={{ fontFamily: "var(--font)" }}>
          <p className="eyebrow">Preview</p><h3>Your galleries</h3><p>Silliness · Summer archive · Little moments</p>
        </div>
        <form onSubmit={event => { event.preventDefault(); void appearance.save(); }}>
          <fieldset disabled={!appearance.ready || appearance.saving}>
            <legend>Personalize your library</legend>
            <label htmlFor="account-font">Default font</label>
            <select id="account-font" value={appearance.draft.fontFamily} onChange={event => appearance.update({ ...appearance.draft, fontFamily: event.target.value })}>
              {fontOptions.map(font => <option key={font.value} value={font.value}>{font.label}</option>)}
            </select>
            <label htmlFor="account-color">Color hue</label>
            <div className="appearance-color">
              <input id="account-color" type="color" value={appearance.draft.accent} onChange={event => appearance.update({ ...appearance.draft, accent: event.target.value })} />
              <span>{appearance.draft.accent.toUpperCase()}</span>
            </div>
            <div className="appearance-actions">
              <button type="submit" disabled={!appearance.dirty}>{appearance.saving ? "Saving…" : "Save appearance"}</button>
              <button type="button" className="secondary" onClick={appearance.cancel} disabled={!appearance.dirty}>Cancel</button>
              <button type="button" className="secondary" onClick={appearance.reset}>Reset to defaults</button>
            </div>
          </fieldset>
          {!appearance.ready && !appearance.error && <p role="status">Loading your preferences…</p>}
          {appearance.error && <p className="error" role="alert">{appearance.error} Refresh the page to try again.</p>}
          <p role="status">{appearance.message || (appearance.dirty ? "Unsaved changes" : "")}</p>
        </form>
      </section>
    </div>
  );
}
